import type { WashWorkOrderSummary } from "@station/contracts";
import { toIstanbulDate } from "@station/core";
import { Alert, Button, EmptyState, MoneyText, SkeletonRows } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CarFront, Plus } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useWashWorkOrders } from "../../../../features/wash/queries.js";
import {
  ReadySmsBadge,
  washSearchShape,
  WashStationPage,
  type WashStationContext,
} from "../../../../features/wash/shared.js";
import {
  DeliverDialog,
  useWorkOrderTransition,
  WorkOrderFormDialog,
} from "../../../../features/wash/work-order-dialogs.js";
import {
  type BoardColumn,
  BOARD_COLUMNS,
  boardColumns,
  displayPlate,
  formatClock,
  forwardTransition,
  paymentState,
} from "../../../../features/wash/wash-values.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatCount } from "../../../../lib/format.js";

const searchSchema = z.object(washSearchShape);

export const Route = createFileRoute("/_app/yikama/is-emirleri/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: WorkOrdersRoute,
});

/**
 * El yıkama iş emri panosu (Prompt 18 web, ADR 0018 §13): Bekliyor / İşlemde / Hazır (+ bugün teslim edilenler) sütunları,
 * tablette büyük dokunma hedefleri. Kartta ileri adım düğmesi (Başla → Hazır → Teslim et); teslim ödeme ya da ödemesiz
 * teslim gerekçesi ister. `wash.work_order.status_changed` (istasyon odası) panoyu tazeler.
 */
function WorkOrdersRoute(): ReactElement {
  const { t } = useTranslation();
  const [creating, setCreating] = useState(false);
  return (
    <WashStationPage
      title={t("menu.washWorkOrders")}
      description={t("wash.workOrders.description")}
      actions={({ permissions }) =>
        permissions.canOperate ? (
          <Button size="lg" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("wash.workOrders.new")}
          </Button>
        ) : null
      }
    >
      {(context) => (
        <>
          <Board context={context} />
          {creating ? (
            <WorkOrderFormDialog open onOpenChange={setCreating} station={context.station} order={null} />
          ) : null}
        </>
      )}
    </WashStationPage>
  );
}

function Board({ context }: { context: WashStationContext }): ReactElement {
  const { t } = useTranslation();
  const stationId = context.station.id;
  const waiting = useWashWorkOrders({ stationId, status: "WAITING" });
  const inProgress = useWashWorkOrders({ stationId, status: "IN_PROGRESS" });
  const ready = useWashWorkOrders({ stationId, status: "READY" });
  // Bugün teslim edilenler: sunucunun TESLİM günü süzgeci (İstanbul) — dün açılıp bugün teslim edilen de gelir.
  const today = toIstanbulDate(new Date());
  const delivered = useWashWorkOrders(
    { stationId, status: "DELIVERED", deliveredFrom: today, deliveredTo: today },
    undefined,
    100,
  );
  const [delivering, setDelivering] = useState<WashWorkOrderSummary | null>(null);
  const queries = [waiting, inProgress, ready, delivered];
  const error = queries.find((query) => query.error)?.error;
  if (error) return <Alert tone="danger">{errorMessage(error)}</Alert>;
  if (queries.some((query) => !query.data)) return <SkeletonRows rows={6} />;
  const columns = boardColumns(
    queries.flatMap((query) => query.data?.items ?? []),
    today,
  );
  const empty = BOARD_COLUMNS.every((column) => columns[column].length === 0);

  return (
    <>
      {empty ? (
        <EmptyState
          headingLevel={2}
          icon={<CarFront />}
          title={t("wash.workOrders.emptyTitle")}
          description={t("wash.workOrders.emptyDescription")}
        />
      ) : null}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="work-order-board">
        {BOARD_COLUMNS.map((column) => (
          <BoardColumnView
            key={column}
            column={column}
            orders={columns[column]}
            stationId={stationId}
            canOperate={context.permissions.canOperate}
            onDeliver={setDelivering}
          />
        ))}
      </div>
      {delivering ? (
        <DeliverDialog open onOpenChange={(open) => !open && setDelivering(null)} order={delivering} />
      ) : null}
    </>
  );
}

function BoardColumnView({
  column,
  orders,
  stationId,
  canOperate,
  onDeliver,
}: {
  column: BoardColumn;
  orders: WashWorkOrderSummary[];
  stationId: string;
  canOperate: boolean;
  onDeliver: (order: WashWorkOrderSummary) => void;
}): ReactElement {
  const { t } = useTranslation();
  const headingId = `column-${column}`;
  return (
    <section
      className="grid content-start gap-3 rounded-lg border bg-muted/30 p-3"
      aria-labelledby={headingId}
    >
      <h2 id={headingId} className="flex items-center justify-between text-sm font-semibold">
        {t(`wash.workOrders.columns.${column}`)}
        <span className="rounded-full bg-card px-2 py-0.5 text-xs tabular">{formatCount(orders.length)}</span>
      </h2>
      {orders.length === 0 ? (
        <p className="px-1 text-sm text-muted-foreground">{t("wash.workOrders.columnEmpty")}</p>
      ) : (
        <ul className="grid gap-3" aria-labelledby={headingId}>
          {orders.map((order) => (
            <li key={order.id}>
              <WorkOrderCard
                order={order}
                stationId={stationId}
                canOperate={canOperate}
                onDeliver={onDeliver}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function WorkOrderCard({
  order,
  stationId,
  canOperate,
  onDeliver,
}: {
  order: WashWorkOrderSummary;
  stationId: string;
  canOperate: boolean;
  onDeliver: (order: WashWorkOrderSummary) => void;
}): ReactElement {
  const { t } = useTranslation();
  const transition = useWorkOrderTransition();
  // İleri adım sunucunun geçiş tablosundan (`allowedTransitions`); teslim gerekçe isterse pencere onu sorar.
  const next = forwardTransition(order.status, order.allowedTransitions);
  const payment = paymentState(order);
  const since =
    order.status === "IN_PROGRESS"
      ? order.inProgressAt
      : order.status === "READY"
        ? order.readyAt
        : order.status === "DELIVERED"
          ? order.deliveredAt
          : order.waitingAt;
  return (
    <article className="grid gap-2 rounded-lg border bg-card p-3 shadow-sm" data-testid="work-order-card">
      <Link
        to="/yikama/is-emirleri/$id"
        params={{ id: order.id }}
        search={{ station: stationId } as never}
        className="grid min-h-11 gap-1 rounded-md focus-visible:outline-2"
      >
        <span className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="font-mono text-lg font-semibold tracking-wide">
            {displayPlate(order.plate, order.personalDataMasked)}
          </span>
          <span className="font-mono text-xs text-muted-foreground">{order.orderNo}</span>
        </span>
        <span className="text-sm">{order.programNames.join(", ")}</span>
        <span className="text-xs text-muted-foreground">
          {[
            order.unitName,
            order.staffName,
            t("wash.workOrders.sinceClock", { at: formatClock(since) }),
            order.estimatedReadyAt && order.status === "IN_PROGRESS"
              ? t("wash.workOrders.etaAt", { at: formatClock(order.estimatedReadyAt) })
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </Link>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <MoneyText value={order.totalAmount} className="font-medium" />
        <span
          className={
            payment === "paid"
              ? "text-success"
              : payment === "warning"
                ? "text-warning"
                : "text-muted-foreground"
          }
        >
          {t(`wash.workOrders.payment.${payment}`)}
        </span>
        {order.status === "READY" || order.status === "DELIVERED" ? (
          <ReadySmsBadge status={order.readySmsStatus} />
        ) : null}
      </div>
      {order.unpaidDeliveryReason ? (
        <p className="text-xs text-muted-foreground">
          {t("wash.workOrders.unpaidNote", { reason: order.unpaidDeliveryReason })}
        </p>
      ) : null}
      {canOperate && next ? (
        <Button
          size="lg"
          className="w-full"
          variant={next === "DELIVERED" ? "outline" : "primary"}
          loading={transition.isPending}
          aria-label={t(`wash.workOrders.forwardFor.${next}`, { no: order.orderNo })}
          onClick={() => (next === "DELIVERED" ? onDeliver(order) : transition.mutate({ order, to: next }))}
        >
          {t(`wash.workOrders.forward.${next}`)}
        </Button>
      ) : null}
      {transition.error ? (
        <p role="alert" className="text-xs font-medium text-destructive">
          {errorMessage(transition.error)}
        </p>
      ) : null}
    </article>
  );
}
